import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, Modal, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// ── TikTok oEmbed embed ───────────────────────────────────────
function TikTokEmbed({ url }) {
  const [html, setHtml] = useState('');
  useEffect(() => {
    if (!url) return;
    fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`)
      .then(r => r.json())
      .then(d => setHtml(d.html || ''))
      .catch(() => setHtml(''));
  }, [url]);
  if (!html) return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="text-sm text-brand-500 underline break-all">{url}</a>
  );
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

const LEVELS      = ['A1','A2','B1','B2'];
const emptyForm   = { title:'', description:'', level:'A1', due_date:'', max_score:100, cohort_id:'' };

export default function AdminHomeworkPage() {
  const { addToast } = useToast();

  const [assignments, setAssignments]   = useState([]);
  const [teacherCohorts, setTeacherCohorts] = useState([]);   // cohorts for this teacher
  const [loading, setLoading]           = useState(true);
  const [form, setForm]                 = useState(emptyForm);
  const [saving, setSaving]             = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [deleteModal, setDelete]        = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [viewingId, setViewing]         = useState(null);
  const [submissions, setSubs]          = useState([]);
  const [subsLoading, setSubsL]         = useState(false);
  const [grades, setGrades]             = useState({});
  const [saving2, setSaving2]           = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      API.get('/homework/assignments').then(r => r.data.assignments || []),
      API.get('/cohorts/teacher').then(r => r.data.cohorts || []).catch(() => []),
    ])
      .then(([hw, cohorts]) => {
        setAssignments(hw);
        setTeacherCohorts(cohorts);
      })
      .catch(() => addToast('Failed to load', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // When level changes, reset cohort_id if the selected cohort
  // no longer matches the new level
  const handleLevelChange = (newLevel) => {
    const cohortStillValid = teacherCohorts.some(
      c => String(c.id) === String(form.cohort_id) && c.level === newLevel
    );
    setForm(p => ({ ...p, level: newLevel, cohort_id: cohortStillValid ? p.cohort_id : '' }));
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.due_date) {
      addToast('Title and due date are required', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = {
        title:       form.title.trim(),
        description: form.description,
        level:       form.level,
        due_date:    form.due_date,
        max_score:   form.max_score,
        cohort_id:   form.cohort_id ? parseInt(form.cohort_id) : null,
      };
      await API.post('/homework/assignments', payload);
      const msg = payload.cohort_id
        ? 'Assignment created for cohort!'
        : 'Assignment created for all students at this level!';
      addToast(msg, 'success');
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/homework/assignments/${deleteModal.id}`);
      addToast('Deleted', 'success');
      setAssignments(p => p.filter(a => a.id !== deleteModal.id));
      setDelete(null);
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setDeleting(false); }
  };

  const openSubmissions = async (id) => {
    setViewing(id);
    setSubsL(true);
    try {
      const { data } = await API.get(`/homework/assignments/${id}/submissions`);
      const subs = data.submissions || [];
      setSubs(subs);
      const init = {};
      subs.forEach(s => { init[s.id] = { score: s.score ?? '', feedback: s.feedback || '' }; });
      setGrades(init);
    } catch { addToast('Failed to load submissions', 'error'); }
    finally { setSubsL(false); }
  };

  const saveGrade = async (subId) => {
    const g = grades[subId];
    if (g.score === '' || g.score === undefined) { addToast('Enter a score', 'error'); return; }
    setSaving2(p => ({ ...p, [subId]: true }));
    try {
      await API.post(`/homework/submissions/${subId}/grade`, {
        score: parseInt(g.score), feedback: g.feedback,
      });
      addToast('Grade saved! Student notified.', 'success');
      setSubs(p => p.map(s => s.id === subId
        ? { ...s, score: parseInt(g.score), feedback: g.feedback } : s));
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving2(p => ({ ...p, [subId]: false })); }
  };

  const viewAsgn = viewingId ? assignments.find(a => a.id === viewingId) : null;

  // Cohorts for the selected level
  const cohortChoices = teacherCohorts.filter(c => c.level === form.level);

  // The selected cohort object (for the info card)
  const selectedCohort = cohortChoices.find(c => String(c.id) === String(form.cohort_id));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionHeader title="Homework" subtitle="Create and grade assignments" />
        <div className="flex gap-2">
          {viewingId && (
            <Button variant="secondary" onClick={() => { setViewing(null); setSubs([]); }}>
              ← Assignments
            </Button>
          )}
          {!viewingId && (
            <Button variant="primary" onClick={() => setShowForm(p => !p)}>
              {showForm ? '× Cancel' : '+ New Assignment'}
            </Button>
          )}
        </div>
      </div>

      {/* ── Create form ──────────────────────────────────────── */}
      {showForm && !viewingId && (
        <Card className="p-6" accent="red">
          <h3 className="text-sm font-bold text-de-black mb-4">Create New Assignment</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Title */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-de-black mb-1.5">Title *</label>
              <input type="text" value={form.title} onChange={e => setF('title', e.target.value)}
                placeholder="e.g. A1 Week 3 — Greetings Worksheet"
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-de-black mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => setF('description', e.target.value)}
                rows={2} placeholder="What should students do?"
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none resize-none" />
            </div>

            {/* Level */}
            <div>
              <label className="block text-xs font-bold text-de-black mb-1.5">Level</label>
              <select value={form.level} onChange={e => handleLevelChange(e.target.value)}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>

            {/* Due date */}
            <div>
              <label className="block text-xs font-bold text-de-black mb-1.5">Due Date *</label>
              <input type="datetime-local" value={form.due_date}
                onChange={e => setF('due_date', e.target.value)}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>

            {/* Max score */}
            <div>
              <label className="block text-xs font-bold text-de-black mb-1.5">Max Score</label>
              <input type="number" min="1" max="1000" value={form.max_score}
                onChange={e => setF('max_score', parseInt(e.target.value))}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>

            {/* ── Assign to Cohort ──────────────────────────── */}
            <div>
              <label className="block text-xs font-bold text-de-black mb-1.5">
                Assign to Cohort
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <select
                value={form.cohort_id}
                onChange={e => setF('cohort_id', e.target.value)}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                <option value="">🌐 All my students (Level {form.level})</option>
                {cohortChoices.map(c => {
                  const days = Array.isArray(c.days) ? c.days : JSON.parse(c.days || '[]');
                  const enrolled = c.current_students || 0;
                  return (
                    <option key={c.id} value={c.id}>
                      {c.name} · {days.map(d => d.slice(0,3)).join('/')} · {enrolled} student{enrolled !== 1 ? 's' : ''}
                    </option>
                  );
                })}
              </select>
              {cohortChoices.length === 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  No Level {form.level} cohorts assigned to you yet.
                </p>
              )}
            </div>

            {/* Selected cohort info card */}
            {selectedCohort && (
              <div className="sm:col-span-2">
                <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <span className="text-blue-500 text-lg flex-shrink-0 mt-0.5">🏫</span>
                  <div>
                    <p className="text-xs font-bold text-blue-800">
                      Assigning to: {selectedCohort.name}
                    </p>
                    <p className="text-xs text-blue-700 mt-0.5">
                      Level {selectedCohort.level} ·&nbsp;
                      {selectedCohort.session} session ·&nbsp;
                      {(Array.isArray(selectedCohort.days)
                        ? selectedCohort.days
                        : JSON.parse(selectedCohort.days || '[]')
                      ).map(d => d.slice(0,3)).join(', ')}
                      &nbsp;·&nbsp;
                      {selectedCohort.current_students || 0} student{(selectedCohort.current_students || 0) !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      📧 Email notification will go to {selectedCohort.current_students || 0} student{(selectedCohort.current_students || 0) !== 1 ? 's' : ''} in this cohort only.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Level-wide info (no cohort selected) */}
            {!form.cohort_id && (
              <div className="sm:col-span-2">
                <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                  <span className="text-gray-400 text-lg flex-shrink-0 mt-0.5">🌐</span>
                  <p className="text-xs text-gray-600">
                    No cohort selected — this assignment will be visible to <strong>all your Level {form.level} students</strong>.
                    Email notifications will be sent to all students assigned to you at this level.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="primary" loading={saving} onClick={handleCreate}>
              Create Assignment
            </Button>
          </div>
        </Card>
      )}

      {/* ── Assignments list ──────────────────────────────────── */}
      {!viewingId && (
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner className="text-brand-500" /></div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No assignments yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-gray-200">
                  <tr>
                    {['Title','Level','Audience','Due','Submissions','Actions'].map(h => (
                      <th key={h}
                        className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assignments.map(a => (
                    <tr key={a.id} className="border-b border-gray-50 hover:bg-surface">
                      <td className="py-3 px-4 font-semibold text-de-black text-xs max-w-xs">
                        {a.title}
                        {a.description && (
                          <p className="text-xs text-gray-400 font-normal mt-0.5 truncate">
                            {a.description}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-bold">
                          {a.level}
                        </span>
                      </td>
                      {/* Audience: cohort name or "All Level X" */}
                      <td className="py-3 px-4">
                        {a.cohort_id && a.cohort_name ? (
                          <span className="flex items-center gap-1 text-xs">
                            <span className="text-blue-500">🏫</span>
                            <span className="font-medium text-blue-700 truncate max-w-[140px]">
                              {a.cohort_name}
                            </span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <span>🌐</span>
                            <span>All Level {a.level}</span>
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(a.due_date).toLocaleDateString('en-GB', {
                          day:'numeric', month:'short', year:'numeric',
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-bold">
                          {a.submission_count || 0} submitted
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="primary"
                            onClick={() => openSubmissions(a.id)}>
                            View
                          </Button>
                          <Button size="sm" variant="danger"
                            onClick={() => setDelete(a)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Submissions panel ─────────────────────────────────── */}
      {viewingId && (
        <Card className="p-6">
          <div className="mb-5">
            <h3 className="text-base font-bold text-de-black">{viewAsgn?.title}</h3>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-xs text-gray-500">
                Max score: {viewAsgn?.max_score} ·
                Due: {viewAsgn?.due_date && new Date(viewAsgn.due_date).toLocaleDateString('en-GB')}
              </p>
              {viewAsgn?.cohort_id && viewAsgn?.cohort_name ? (
                <span className="flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                  🏫 {viewAsgn.cohort_name}
                </span>
              ) : (
                <span className="text-xs text-gray-400">🌐 All Level {viewAsgn?.level}</span>
              )}
            </div>
          </div>

          {subsLoading ? (
            <div className="flex justify-center py-8"><Spinner className="text-brand-500" /></div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <p className="text-2xl mb-2">📭</p>
              No submissions yet.
            </div>
          ) : (
            <div className="space-y-5">
              {submissions.map(sub => {
                const g = grades[sub.id] || { score: '', feedback: '' };
                return (
                  <div key={sub.id} className="border border-gray-200 rounded-xl p-4">
                    {/* Student info */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-brand-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {sub.student_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-de-black">{sub.student_name}</p>
                          <p className="text-xs text-gray-400">{sub.student_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {sub.score != null ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                            Graded: {sub.score}/{viewAsgn?.max_score}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold">
                            Awaiting grade
                          </span>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(sub.submitted_at).toLocaleDateString('en-GB', {
                            day:'numeric', month:'short',
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Submission content */}
                    {sub.submission_type === 'file' && sub.file_url && (
                      <a href={sub.file_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-surface border border-gray-200 rounded-xl text-xs font-semibold text-de-black hover:border-brand-300 transition-colors mb-3">
                        📄 Download Submission
                      </a>
                    )}
                    {sub.submission_type === 'tiktok' && sub.tiktok_url && (
                      <div className="mb-3"><TikTokEmbed url={sub.tiktok_url} /></div>
                    )}

                    {/* Grading row */}
                    <div className="flex gap-3 items-end mt-3 pt-3 border-t border-gray-100">
                      <div className="w-32">
                        <label className="block text-xs font-bold text-gray-600 mb-1">
                          Score (/{viewAsgn?.max_score})
                        </label>
                        <input type="number" min="0" max={viewAsgn?.max_score}
                          value={g.score}
                          onChange={e => setGrades(p => ({ ...p, [sub.id]: { ...g, score: e.target.value } }))}
                          className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-bold text-gray-600 mb-1">
                          Feedback
                        </label>
                        <input type="text"
                          value={g.feedback}
                          placeholder="Write feedback for the student…"
                          onChange={e => setGrades(p => ({ ...p, [sub.id]: { ...g, feedback: e.target.value } }))}
                          className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none" />
                      </div>
                      <Button size="sm" variant="primary"
                        loading={saving2[sub.id]}
                        onClick={() => saveGrade(sub.id)}>
                        Save Grade
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Delete confirm */}
      <Modal isOpen={!!deleteModal} onClose={() => setDelete(null)}
        title="Delete Assignment" size="sm">
        {deleteModal && (
          <div>
            <p className="text-sm text-de-text mb-2">
              Delete <strong>"{deleteModal.title}"</strong>?
            </p>
            <p className="text-xs text-brand-500 mb-4">
              ⚠ All submissions for this assignment will also be deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setDelete(null)}>Cancel</Button>
              <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
